import { Button, Flex, Text, TextField, Card, Badge } from "@radix-ui/themes";
import Switch from "@/components/shared/inputs/switch";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useState, useCallback } from "react";
import { JsonSchema7Property, JsonSchema7Type } from "./types";
import { TypeSelector } from "./TypeSelector";
import { ValidationEditor } from "./ValidationEditor";
import { generateId } from "./utils";
import { RadixColor } from "@/components/radixColors";

const typeToColor: Record<JsonSchema7Type, RadixColor> = {
  string: "blue",
  number: "green",
  integer: "green",
  boolean: "purple",
  array: "orange",
  object: "red",
  null: "gray",
};

interface PropertyEditorProps {
  property: JsonSchema7Property;
  onChange: (property: JsonSchema7Property) => void;
  onDelete: () => void;
  depth?: number;
}

export function PropertyEditor({
  property,
  onChange,
  onDelete,
  depth = 0,
}: PropertyEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleNameChange = useCallback(
    (name: string) => {
      onChange({ ...property, name });
    },
    [property, onChange]
  );

  const handleTypeChange = useCallback(
    (type: any) => {
      onChange({ ...property, type });
    },
    [property, onChange]
  );

  const handleTitleChange = useCallback(
    (title: string) => {
      onChange({ ...property, title });
    },
    [property, onChange]
  );

  const handleDescriptionChange = useCallback(
    (description: string) => {
      onChange({ ...property, description });
    },
    [property, onChange]
  );

  const handleRequiredChange = useCallback(
    (required: boolean) => {
      onChange({ ...property, required });
    },
    [property, onChange]
  );

  const handleValidationChange = useCallback(
    (validation: any) => {
      onChange({ ...property, validation });
    },
    [property, onChange]
  );

  const handleDefaultChange = useCallback(
    (defaultValue: string) => {
      let parsedValue: any = defaultValue;
      try {
        parsedValue = JSON.parse(defaultValue);
      } catch {
        // Keep as string if not valid JSON
      }
      onChange({ ...property, default: parsedValue });
    },
    [property, onChange]
  );

  const handleAddNestedProperty = useCallback(() => {
    if (property.type !== "object") {
      return;
    }

    const newProperty: JsonSchema7Property = {
      id: generateId(),
      name: `nested_${Date.now()}`,
      type: "string",
      required: false,
      validation: {},
    };

    const newProperties = property.properties
      ? [...property.properties, newProperty]
      : [newProperty];

    onChange({
      ...property,
      properties: newProperties,
    });
  }, [property, onChange]);

  const handleAddArrayItem = useCallback(
    (itemType: JsonSchema7Type) => {
      if (property.type !== "array") {
        return;
      }

      const newItem: JsonSchema7Property = {
        id: generateId(),
        name: `item_${Date.now()}`,
        type: itemType,
        validation: {},
      };

      onChange({ ...property, items: newItem });
    },
    [property, onChange]
  );

  const handleUpdateNestedProperty = useCallback(
    (nestedProperty: JsonSchema7Property) => {
      const newProperties = property.properties?.map((p) =>
        p.id === nestedProperty.id ? nestedProperty : p
      );

      onChange({
        ...property,
        properties: newProperties,
      });
    },
    [property, onChange]
  );

  const handleDeleteNestedProperty = useCallback(
    (nestedPropertyId: string) => {
      const newProperties = property.properties?.filter(
        (p) => p.id !== nestedPropertyId
      );

      onChange({ ...property, properties: newProperties });
    },
    [property, onChange]
  );

  const handleUpdateArrayItem = useCallback(
    (arrayItem: JsonSchema7Property) => {
      if (property.type !== "array") {
        return;
      }

      onChange({ ...property, items: arrayItem });
    },
    [property, onChange]
  );

  const isObject = property.type === "object";
  const isArray = property.type === "array";

  return (
    <Card style={{ marginLeft: depth * 20 }}>
      <Flex direction="column" gap="3">
        <Flex
          align="center"
          gap="3"
          onClick={() => setIsExpanded(!isExpanded)}
          className="cursor-pointer"
        >
          <Button
            variant="ghost"
            size="1"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </Button>

          <Flex align="center" gap="2" style={{ flex: 1, minWidth: 0 }}>
            <Badge
              color={typeToColor[property.type]}
              size="2"
              className="font-mono"
            >
              {property.type}
            </Badge>
            <Text size="2" weight="medium">
              {property.name || "Unnamed property"}
            </Text>
          </Flex>

          <Flex align="center" gap="2">
            {property.required && (
              <Badge color="red" size="2" className="font-weight-bold">
                Required
              </Badge>
            )}
            {property.title && (
              <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                {property.title}
              </Text>
            )}
          </Flex>

          <Button variant="ghost" color="red" size="1" onClick={onDelete}>
            <TrashIcon />
          </Button>
        </Flex>

        {isExpanded && (
          <Collapsible.Root open>
            <Collapsible.Content>
              <Flex direction="column" gap="3">
                {/* Basic Properties */}
                <Flex direction="column" gap="2">
                  <Flex gap="2" align="center">
                    <TypeSelector
                      value={property.type}
                      onChange={handleTypeChange}
                    />
                    <TextField.Root
                      placeholder="Property name"
                      value={property.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Switch
                      value={property.required || false}
                      onChange={handleRequiredChange}
                    />
                    <Text size="1">Required</Text>
                  </Flex>

                  <TextField.Root
                    placeholder="Title (optional)"
                    value={property.title || ""}
                    onChange={(e) => handleTitleChange(e.target.value)}
                  />

                  <TextField.Root
                    placeholder="Description (optional)"
                    value={property.description || ""}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                  />

                  <TextField.Root
                    placeholder="Default value (JSON format)"
                    value={
                      property.default !== undefined
                        ? JSON.stringify(property.default)
                        : ""
                    }
                    onChange={(e) => handleDefaultChange(e.target.value)}
                  />
                </Flex>

                {/* Validation Editor */}
                <ValidationEditor
                  validation={property.validation || {}}
                  type={property.type}
                  onChange={handleValidationChange}
                />

                {/* Nested Properties */}
                {isObject && (
                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2" weight="medium">
                        Properties
                      </Text>
                      <Button
                        size="1"
                        variant="outline"
                        onClick={handleAddNestedProperty}
                      >
                        <PlusIcon />
                        Add Property
                      </Button>
                    </Flex>

                    {property.properties?.map((nestedProp) => (
                      <PropertyEditor
                        key={nestedProp.id}
                        property={nestedProp}
                        onChange={handleUpdateNestedProperty}
                        onDelete={() =>
                          handleDeleteNestedProperty(nestedProp.id)
                        }
                        depth={depth + 1}
                      />
                    ))}
                  </Flex>
                )}

                {/* Array Items */}
                {isArray && (
                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2" weight="medium">
                        Array Items
                      </Text>
                      <Button
                        size="1"
                        variant="outline"
                        onClick={() => handleAddArrayItem("string")}
                      >
                        <PlusIcon />
                        Add Item Type
                      </Button>
                    </Flex>

                    {property.items && (
                      <PropertyEditor
                        property={property.items}
                        onChange={handleUpdateArrayItem}
                        onDelete={() =>
                          onChange({ ...property, items: undefined })
                        }
                        depth={depth + 1}
                      />
                    )}
                  </Flex>
                )}
              </Flex>
            </Collapsible.Content>
          </Collapsible.Root>
        )}
      </Flex>
    </Card>
  );
}
